import type { TemplateLanguage } from '@/types/broadcast.ts';

/** What the editor holds. `name` is fixed once the template exists. */
export interface TemplateFormData {
  name: string;
  body: string;
  language: TemplateLanguage;
}

export interface TemplateFormErrors {
  name: string;
  body: string;
}

/** The body a template may hold, and the counter under the field. */
export const TEMPLATE_BODY_LIMIT = 1024;

export const NAME_LIMIT = 100;

export function emptyTemplateForm(): TemplateFormData {
  return { name: '', body: '', language: 'nl' };
}

export function noTemplateFormErrors(): TemplateFormErrors {
  return { name: '', body: '' };
}

/**
 * The name is the key the template is stored and fetched under, so it stays a
 * slug: lowercase, digits and underscores.
 */
export function validateTemplateForm(form: TemplateFormData): TemplateFormErrors {
  const errors = noTemplateFormErrors();

  if (!form.name.trim()) {
    errors.name = 'Template name is required';
  } else if (form.name.length > NAME_LIMIT) {
    errors.name = `Template name must be ${NAME_LIMIT} characters or less`;
  } else if (!/^[a-z0-9_]+$/.test(form.name)) {
    errors.name =
      'Template name can only contain lowercase letters, numbers, and underscores';
  }

  if (!form.body.trim()) {
    errors.body = 'Template body is required';
  } else if (form.body.length > TEMPLATE_BODY_LIMIT) {
    errors.body = `Template body must be ${TEMPLATE_BODY_LIMIT} characters or less`;
  }

  return errors;
}

export function hasTemplateFormErrors(errors: TemplateFormErrors): boolean {
  return errors.name !== '' || errors.body !== '';
}
